<template>
    <v-card-text :class="`${secondColor}--text pb-0`" v-if="show && (hasPower || maximumPower > 0)">
        <div class="gauge" v-if="hasPower">
            <svg viewBox="0 0 200 118" class="gauge-svg">
                <path :d="arcPath" class="gauge-track" />
                <path :d="arcPath" class="gauge-fill" :style="{ strokeDasharray: arcLength, strokeDashoffset: fillOffset }" />
                <line v-if="showPeak" :x1="peak.x1" :y1="peak.y1" :x2="peak.x2" :y2="peak.y2" class="gauge-peak" />
                <text x="100" y="80" text-anchor="middle" class="gauge-value">{{ powerText.value }}</text>
                <text x="100" y="98" text-anchor="middle" class="gauge-unit">{{ powerText.unit }}</text>
                <text x="20" y="116" text-anchor="middle" class="gauge-scale">0</text>
                <text x="180" y="116" text-anchor="middle" class="gauge-scale">{{ scaleText }}</text>
            </svg>
            <v-icon :color="secondColor" :class="['gauge-flash', { 'flowing': power > 0 }]">flash_on</v-icon>
        </div>
        <div class="text-center" v-if="hasPower">Potência instantânea</div>

        <div class="text-center text-caption max-power" v-if="maximumPower > 0">
            Potência máxima: {{ format(maximumPower).value }} {{ format(maximumPower).unit }}
        </div>
    </v-card-text>
</template>

<script>
// Faixas comuns de potência de carregadores (kW), usadas como fundo de escala do medidor
const SCALES = [3.7, 7.4, 11, 22, 30, 40, 60, 80, 120, 150, 180, 240, 300, 350];
const RADIUS = 80;
const CENTER_X = 100;
const CENTER_Y = 100;

export default {
    props: {
        plug: {
            required: true,
        },
        secondColor: {
            required: true,
        },
        show: {
            required: true,
        },
    },

    data() {
        return {
            arcPath: `M ${CENTER_X - RADIUS} ${CENTER_Y} A ${RADIUS} ${RADIUS} 0 0 1 ${CENTER_X + RADIUS} ${CENTER_Y}`,
            arcLength: Math.PI * RADIUS,
        };
    },

    computed: {
        power() {
            return Math.max(Number(this.plug.power || 0), 0);
        },
        maximumPower() {
            return Number(this.plug.maximumPower || 0);
        },
        hasPower() {
            return this.plug.power != null && this.plug.status == "Charging";
        },
        scaleKw() {
            const kw = Math.max(this.power, this.maximumPower) / 1000;
            return SCALES.find((scale) => kw <= scale) || Math.ceil(kw / 50) * 50;
        },
        scaleText() {
            return `${this.scaleKw.toLocaleString("pt-BR")} kW`;
        },
        fillOffset() {
            const fraction = Math.min(this.power / 1000 / this.scaleKw, 1);
            return this.arcLength * (1 - fraction);
        },
        // Só mostra o marcador quando o pico se distancia da potência atual, senão ele se sobrepõe ao fim do arco
        showPeak() {
            return this.maximumPower > 0 && (this.maximumPower - this.power) / 1000 > this.scaleKw * 0.03;
        },
        peak() {
            const fraction = Math.min(this.maximumPower / 1000 / this.scaleKw, 1);
            const angle = Math.PI * (1 - fraction);
            const point = (radius) => ({
                x: CENTER_X + radius * Math.cos(angle),
                y: CENTER_Y - radius * Math.sin(angle),
            });
            const inner = point(RADIUS - 13);
            const outer = point(RADIUS + 9);
            return { x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y };
        },
        powerText() {
            return this.format(this.power);
        },
    },

    methods: {
        format(watts) {
            if (watts > 1000) {
                return { value: (watts / 1000).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }), unit: "kW" };
            }
            return { value: Math.round(watts).toLocaleString("pt-BR"), unit: "W" };
        },
    },
}
</script>

<style scoped>
.max-power {
    opacity: 0.75;
}

.gauge {
    position: relative;
    max-width: 180px;
    margin: 0 auto;
}

.gauge-svg {
    display: block;
    width: 100%;
    overflow: visible;
}

.gauge-track,
.gauge-fill {
    fill: none;
    stroke: currentColor;
    stroke-width: 14;
    stroke-linecap: round;
}

.gauge-track {
    opacity: 0.2;
}

.gauge-fill {
    transition: stroke-dashoffset 1.2s ease-out;
}

.gauge-peak {
    stroke: currentColor;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-dasharray: 3 3;
}

.gauge-value {
    fill: currentColor;
    font-size: 30px;
    font-weight: 700;
}

.gauge-unit {
    fill: currentColor;
    font-size: 13px;
    opacity: 0.85;
}

.gauge-scale {
    fill: currentColor;
    font-size: 10px;
    opacity: 0.7;
}

.gauge-flash {
    position: absolute;
    top: 34%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0.4;
}

.gauge-flash.flowing {
    animation: flow 1.6s ease-in-out infinite;
}

@keyframes flow {
    0%,
    100% {
        opacity: 0.3;
        transform: translate(-50%, -50%) scale(0.9);
    }
    50% {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1.15);
    }
}
</style>
